import { useEffect, useState, type ReactNode } from 'react';
import {
  activate,
  fetchAndActivate,
  getBoolean,
  onConfigUpdate,
  setCustomSignals,
  type RemoteConfig,
} from 'firebase/remote-config';
import { getFirebaseRemoteConfig, getRemoteConfigEnvironment } from '@/core/firebase/config';
import { getStrictContext } from '@/lib/get-strict-context';

/** Remembers which environment last activated, so a failed fetch can't reuse the other one's values. */
const ENVIRONMENT_STORAGE_KEY = 'robinhoot-remote-config-env';

const STANDARD_FAVICON = '/icons/icon-96x96.png';
const XMAS_FAVICON = '/icons/xmas/icon-96x96.png';

export interface SeasonalFlags {
  /** The year in review is open. Follows Remote Config `wrap_enabled` for this environment. */
  wrapEnabled: boolean;
  /** The in-app Christmas theme: logos, hats, header lights, treats icon, corner tree, tab favicon, and push icons. */
  xmasEnabled: boolean;
  /** False until the first Remote Config fetch settles. */
  ready: boolean;
}

const [SeasonalFlagsContext, useSeasonalFlags] =
  getStrictContext<SeasonalFlags>('SeasonalFlagsProvider');

function readFlags(remoteConfig: RemoteConfig | null): SeasonalFlags {
  return {
    wrapEnabled: !!remoteConfig && getBoolean(remoteConfig, 'wrap_enabled'),
    xmasEnabled: !!remoteConfig && getBoolean(remoteConfig, 'xmas_enabled'),
    ready: true,
  };
}

function storedEnvironment(): string | null {
  try {
    return localStorage.getItem(ENVIRONMENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function rememberEnvironment(environment: string) {
  try {
    localStorage.setItem(ENVIRONMENT_STORAGE_KEY, environment);
  } catch {
    // localStorage unavailable
  }
}

function applyFavicon(xmasEnabled: boolean) {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link) link.href = xmasEnabled ? XMAS_FAVICON : STANDARD_FAVICON;
}

function applyXmasClass(xmasEnabled: boolean) {
  document.documentElement.classList.toggle('xmas', xmasEnabled);
}

/** Loads the seasonal flags from Remote Config and keeps them live while the app is open. */
export function SeasonalFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<SeasonalFlags>({
    wrapEnabled: false,
    xmasEnabled: false,
    ready: false,
  });

  useEffect(() => {
    let active = true;
    let unsubscribe = () => {};

    const apply = (remoteConfig: RemoteConfig | null) => {
      if (!active) return;
      const next = readFlags(remoteConfig);
      setFlags(next);
      applyFavicon(next.xmasEnabled);
      applyXmasClass(next.xmasEnabled);
    };

    getFirebaseRemoteConfig().then(async remoteConfig => {
      if (!active) return;
      if (!remoteConfig) {
        apply(null);
        return;
      }

      const environment = getRemoteConfigEnvironment();
      // Signals persist per site, so set this on every start. It selects the Development
      // condition for local and staging, and the production defaults otherwise.
      await setCustomSignals(remoteConfig, { env: environment });
      if (!active) return;
      const cacheMatchesEnvironment = storedEnvironment() === environment;

      fetchAndActivate(remoteConfig)
        .then(() => {
          rememberEnvironment(environment);
          apply(remoteConfig);
        })
        .catch(error => {
          console.error('Failed to fetch Remote Config:', error);
          apply(cacheMatchesEnvironment ? remoteConfig : null);
        });

      unsubscribe = onConfigUpdate(remoteConfig, {
        next: () => {
          activate(remoteConfig)
            .then(() => {
              rememberEnvironment(environment);
              apply(remoteConfig);
            })
            .catch(error => {
              console.error('Failed to activate Remote Config:', error);
            });
        },
        error: error => {
          console.error('Remote Config live updates stopped:', error);
        },
        complete: () => {},
      });
    });

    return () => {
      active = false;
      unsubscribe();
      applyXmasClass(false);
    };
  }, []);

  return <SeasonalFlagsContext value={flags}>{children}</SeasonalFlagsContext>;
}

export { useSeasonalFlags };
